import React, { useState } from "react";
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import AppTheme from '../shared-theme/AppTheme';
import Banner from "../components/Banner";

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const GradientBackground = styled('div')({
  position: 'fixed',
  inset: 0,
  background: 'radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)',
  zIndex: -1,
});

const SettingsContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100dvh',
  padding: theme.spacing(2),
  marginTop: '50px',
  overflowY: 'auto',
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
}));

const Terms: React.FC = () => {
  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <GradientBackground />
      <Banner />
      <SettingsContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
          >
            Terms and Conditions
          </Typography>
          <Typography variant="h5">Last Updated: 08/04/2025</Typography>

          <Typography variant="body1">
            When we say "Company", "we", "our", or "us" in this document, we are referring to Event Spy.
          </Typography>
          <Typography variant="body1">
            When we say "Services", we mean our website, event-spy.com and/or any other event-spy.com domains.
          </Typography>
          <Typography variant="body1">
            When we say "You" or "your", we are referring to the person that owns an account with one or more of our Services.
          </Typography>

          <Typography variant="body1" >
            We may update these Terms of Service ("Terms") in the future. We will email all customers to notify them that Terms have been changed.
            Typically these changes have been to clarify some of these terms by linking to an expanded related policy. Whenever we make a significant change to our policies, we will refresh the date at the top of this page and take any other appropriate steps to notify account holders.
          </Typography>

          <Typography variant="body1">
            When you use our Services, now or in the future, you are agreeing to the latest Terms. There may be times where we do not exercise or enforce a right or provision of the Terms; however, that does not mean we are waiving that right or provision. These Terms do contain a limitation of our liability.
          </Typography>

          <Typography variant="body1" paragraph>
            If you violate any of the Terms, we may terminate your account. That’s a broad statement and it means you need to place a lot of trust in us. We do our best to deserve that trust by being open about who we are and keeping an open door to your feedback at <a href="mailto:support@event-spy.com">support@event-spy.com</a>.
          </Typography>

          <Typography variant="h5" >Account Terms</Typography>
          <Typography variant="body1">
            1. You are responsible for maintaining the security of your account and password and for ensuring that any of your users do the same. The Company cannot and will not be liable for any loss or damage from your failure to comply with this security obligation.
          </Typography>
          <Typography variant="body1">
            2. You may not have multiple accounts with us.
          </Typography>
          <Typography variant="body1">
            3. You are responsible for all content posted to and activity that occurs under your account, including content posted by and activity of any users in your account.
          </Typography>
          <Typography variant="body1" paragraph>
            4. You must be a human. Accounts registered by “bots” or other automated methods are not permitted.
          </Typography>


          <Typography variant="h5" >Payment, Refunds, and Plan Changes</Typography>
          <Typography variant="body1" >
            1. If you are using a free version of one of our Services, it is really free: we do not ask you for your credit card and — just like for customers who pay for our Services — we do not sell your data.
          </Typography>
          <Typography variant="body1" >
            2. If you are upgrading from a free plan to a paid plan, we will charge your card immediately and your billing cycle starts on the day of upgrade. For other upgrades or downgrades in plan level, the new rate starts from the next billing cycle.
          </Typography>
          <Typography variant="body1" >
            3. All fees are inclusive of all taxes, levies, or duties imposed by taxing authorities. Where required, we collect taxes on behalf of the taxing authority and remit those taxes to taxing authorities.
          </Typography>
          <Typography variant="body1" >
            4. Paid plans automatically renew at the end of each billing cycle unless canceled prior to renewal.
          </Typography>
          <Typography variant="body1" paragraph>
            5. We do not typically offer refunds, but under special circumstances such as service unavailability or major bugs, we might. Reach out to <a href="mailto:support@event-spy.com">support@event-spy.com</a> for questions.
          </Typography>

          <Typography variant="h5" >Account Termination</Typography>
          <Typography variant="body1" >
            1. You are solely responsible for properly terminating your account. You can terminate your account by emailing <a href="mailto:support@event-spy.com">support@event-spy.com</a>.
          </Typography>
          <Typography variant="body1" >
            2. All of your content will be inaccessible from the Services immediately upon account termination. Within 30 days, all content will be permanently deleted from active systems and logs. We cannot recover this information once it has been permanently deleted.
          </Typography>
          <Typography variant="body1" >
            3. If you terminate your account before the end of your current paid up month, your termination will take effect immediately, and you will not be charged again. We do not automatically prorate unused time in the last billing cycle.
          </Typography>
          <Typography variant="body1" >
            4. We have the right to suspend or terminate your account and refuse any and all current or future use of our Services for any reason at any time. Suspension means you will not be able to access the account or any content in the account. Termination will furthermore result in the deletion of your account or your access to your account, and the forfeiture and relinquishment of all content in your account. We also reserve the right to refuse the use of the Services to anyone for any reason at any time.
          </Typography>
          <Typography variant="body1" paragraph>
            5. Verbal, physical, written or other abuse (including threats of abuse or retribution) of a Company employee or officer will result in immediate account termination.
          </Typography>

          <Typography variant="h5" >Modifications to the Service and Prices</Typography>
          <Typography variant="body1" >
            1. We may redesign or shutdown a part of our Services because we think it could be better or we decide to close new signups of a product. We reserve the right at any time to modify or discontinue, temporarily or permanently, any part of our Services with or without notice.
          </Typography>
          <Typography variant="body1" paragraph>
            2. Sometimes we change the pricing structure for our products. When we do that, we tend to exempt existing customers from those changes. However, we may choose to change the prices for existing customers. If we do so, we will give at least 30 days notice and will notify you via the email address on record. We may also post a notice about changes on our websites or the affected Services themselves.
          </Typography>

          <Typography variant="h5" >Uptime, Security, and Privacy</Typography>
          <Typography variant="body1" >
            1. Your use of the Services is at your sole risk. We provide these Services on an “as is” and “as available” basis.
          </Typography>
          <Typography variant="body1" >
            2. We reserve the right to temporarily disable your account if your usage significantly exceeds the average usage of other customers of the Services. Of course, we’ll reach out to the account owner before taking any action except in rare cases where the level of use may negatively impact the performance of the Service for other customers.
          </Typography>
          <Typography variant="body1" >
            3. We take many measures to protect and secure your data through backups, redundancies, and encryption. We enforce encryption for data transmission from the public Internet.
          </Typography>
          <Typography variant="body1" paragraph>
            4. When you use our Services, you entrust us with your data. We take that trust to heart. You agree that Event Spy may process your data as described in our Privacy Policy and for no other purpose
          </Typography>

          <Typography variant="h5" >Features and Bugs</Typography>
          <Typography variant="body1" >
            We design our Services with care, based on our own experience and the experiences of customers who share their time and feedback. However, there is no such thing as a service that pleases everybody. We make no guarantees that our Services will meet your specific requirements or expectations.
          </Typography>
          <Typography variant="body1" paragraph>
            We also test all of our features extensively before shipping them. As with any software, our Services inevitably have some bugs. We track the bugs reported to us and work through priority ones, especially any related to security or privacy. Not all reported bugs will get fixed and we don’t guarantee completely error-free Services.
          </Typography>

          <Typography variant="h5" >Liability</Typography>
          <Typography variant="body1" >
            We mention liability throughout these Terms but to put it all in one section:
          </Typography>
          <Typography
            variant="body1"

            sx={{
              fontWeight: 'bold',
              fontStyle: 'italic'
            }}
          >
            You expressly understand and agree that the Company shall not be liable, in law or in equity, to you or to any third party for any direct, indirect, incidental, lost profits, special, consequential, punitive or exemplary damages, including, but not limited to, damages for loss of profits, goodwill, use, data or other intangible losses (even if the Company has been advised of the possibility of such damages), resulting from: (i) the use or the inability to use the Services; (ii) the cost of procurement of substitute goods and services resulting from any goods, data, information or services purchased or obtained or messages received or transactions entered into through or from the Services; (iii) unauthorized access to or alteration of your transmissions or data; (iv) statements or conduct of any third party on the service; (v) or any other matter relating to these Terms or the Services, whether as a breach of contract, tort (including negligence whether active or passive), or any other theory of liability.
          </Typography>

          <Typography variant="body1" sx={{ mt: 2 }}>
            If you have a question about any of these Terms, please email us at{' '}
            <a href="mailto:support@event-spy.com">support@event-spy.com</a>.
          </Typography>
        </Card>
      </SettingsContainer>
    </AppTheme >
  );
};

export default Terms;
